import {Spec} from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {makeFormattedTextSearchResultData} from 'notebook/mediocre/formatted-text/FormattedTextSearchResults';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import CodeEditable from './CodeEditable';

function codeSelectionInit(this: NoteEditor, item: NotePart): {selection: BenchlingSelection} {
  const cid = this.props.getCid(item);
  // @ts-expect-error: TODO - getContent doesn't have an obvious typesafe implementation
  const start = this.props.getContent(item).code.length;
  const end = start;
  return {selection: {cid, start, end}};
}

function codeSelectionAll(this: NoteEditor, item: NotePart): {selection: BenchlingSelection} {
  const cid = CodeEditable.hiddenFocusCid(this.props.getCid(item));
  return {selection: {cid, start: 0, end: 0}};
}

const CODE_PLUGIN_COMPONENT = {
  required: [
    'inputBoxCursorHandler',
    'codeTabHandler',
    'codeSplitHandler',
    'inputBoxDeleteHandler',
    'setSelectionAbove',
    'setSelectionBelow',
    'insertItemHandler',
    'itemUpdateHandler',
    'moveHandler',
    'copyItemWithoutText',
    'isLockedWithEditWarn',
  ],

  // When moving into a code box from another element, the entire code box should be selected instead of
  // moving into the code text (this allows the user to treat the code box more like an element that can
  // be moved through than a piece of text). selectionAtStart/End set to all enables this.
  selectionAtStart: codeSelectionAll,
  selectionAtEnd: codeSelectionAll,
  selectionInit: codeSelectionInit,
  selectionAll: codeSelectionAll,

  default(): unknown {
    return {code: ''};
  },

  skipVirtualizationDueToPerformantRender(item: NotePart) {
    // Skipping this since it is relatively fast to render a text-only component. See justification for
    // 'client/notebook/notes/plugins/text-block/TextPlugin.tsx'.
    return true;
  },

  make(
    this: NoteEditor,
    item: NotePart,
    index: number,
    editorProps: NoteEditorProps,
    itemProps: object,
    searchResultProps: MediocreSearchResultProps
  ): JSX.Element {
    // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
    const cid = editorProps.getCid(item);
    const {code} = editorProps.getContent(item) as {code: string};
    return (
      <CodeEditable
        key={cid}
        cid={cid}
        code={code}
        index={index}
        storeState={editorProps.storeState}
        restoreState={editorProps.restoreState}
        requestSelection={editorProps.requestSelection}
        locked={editorProps.locked || this.isProtectedAgainstEdit()}
        splitHandler={this.codeSplitHandler}
        updateHandler={this.codeUpdateHandler}
        tabHandler={this.codeTabHandler}
        deleteHandler={this.inputBoxDeleteHandler}
        cursorHandler={this.inputBoxCursorHandler}
        moveHandler={this.moveHandler}
        setSelectionAbove={this.setSelectionAbove}
        setSelectionBelow={this.setSelectionBelow}
        insertItemHandler={this.insertItemHandler}
        setClipboard={this.copyItemWithoutText}
        isLockedWithEditWarn={this.isLockedWithEditWarn}
        {...searchResultProps}
      />
    );
  },
};

const CODE_PLUGIN: NoteEditorPlugin = {
  type: NoteConstants.CODE,
  typeClass: 'is-code',
  title: 'Code',
  icon: 'note-code',
  component: CODE_PLUGIN_COMPONENT,
  linkable: false,
  canPasteFromOtherTenant: true,
  itemToHTML($el: JQuery, item: NotePart & {code: string}): JQuery {
    $el.append($('<pre>').text(item.code));
    return $el;
  },
  itemFromHTML($el: JQuery, item: NotePart & {code: string}): NotePart {
    return item;
  },
  itemToText(item: NotePart & {code: string}): string {
    return item.code;
  },
  getContent({code}: {code: string | null}): {code: string | null; blockText: string} {
    return {code, blockText: code ?? ''};
  },
  setContent({code}: {code: string}): Spec<NotePart> {
    return {
      $merge: {code},
    };
  },
  *getSearchResults(item: NotePart & {code: string}, matcher: TextMatcher): Generator<MediocreSearchResult> {
    for (const match of matcher.findAllMatches(item.code)) {
      const [matchedText] = match;
      const start = match.index;
      const end = start + matchedText.length;
      yield {
        index: [item.cid],
        data: makeFormattedTextSearchResultData(start, end),
        selection: {selection: {cid: item.cid, start, end}},
        uuid: uuidv4(),
      };
    }
  },
};

export default CODE_PLUGIN;
